import { useCallback, useContext, useEffect, useState } from "react";
import axios from "../../../api/axios";
import { sleep } from "../../../functions";
import { useNavigate } from "react-router-dom";
import CustomerContext from "./context";
import useAlert from "../../../globalComponents/useAlerts";

export const useCustomerManager = () => {
  const navigate = useNavigate();
  const { showLoading, showAlert, dismissLoading } = useAlert();
  const [loading, setLoading] = useState(true);

  const [customer, setCustomer] = useState({
    name: "",
    phone: "",
    document: "",
    email: "",
    birthDate: "",
    address: {
      street: "",
      number: "",
      complement: "",
      reference: "",
      neighborhood: "",
      zipCode: "",
      city: "",
      state: "",
    },
  });

  const [balances, setBalances] = useState({
    totalEarned: 0,
    totalRedeemed: 0,
    balance: 0,
  });

  const [transactionsRequest, setTransactionsRequest] = useState({
    loading: true,
    data: [],
    page: 0,
    limit: 20,
  });

  const [rewardsRequest, setRewardsRequest] = useState({
    loading: true,
    data: [],
  });

  const getCustomer = async () => {
    try {
      const data = await axios.get("/user-customer/");

      setCustomer((c) => ({ ...c, ...data }));
    } catch (err) {
      showAlert({
        title: "Erro",
        description: "Verifique os parâmetros enviados e tente novamente.",
        onConfirm: navigate("../"),
      });
    } finally {
      setTimeout(() => setLoading(false), 1500);
    }
  };

  const updateCustomer = useCallback(async (form, cb) => {
    try {
      showLoading();

      const data = await axios.put("/user-customer", form);

      setCustomer((c) => ({ ...c, ...data }));
      if (typeof cb === "function") cb();

      await sleep(1000);

      showAlert({
        title: "Concluído",
        description: "Informações atualizadas com sucesso",
      });
    } catch (err) {
      showAlert({ title: "Houve um erro", description: err.message });
    } finally {
      dismissLoading();
    }
  }, []);

  const getBalances = useCallback(async () => {
    try {
      const data = await axios.get("/user-customer/balances");

      setBalances({ ...data });
    } catch (err) {
      showAlert({
        title: "Erro",
        description:
          "Não foi possível obter saldos de pontos. Verifique os parâmetros enviados e tente novamente.",
      });
    } finally {
    }
  }, []);

  const getTransactions = useCallback(async () => {
    const mData = { ...transactionsRequest };

    try {
      //setTransactionsRequest((t) => ({ ...t, loading: true }));
      transactionsRequest.loading = true;
      //showLoading();
      const res = await axios.get(
        `/user-customer/transactions?&page=${transactionsRequest.page + 1}&limit=${transactionsRequest.limit}`,
      );

      const { data, error, totalItems, totalPages } = res;

      if (error) throw new Error(error);

      const formattedData = data.map(({ ...p }, index) => ({
        ...p,
        id: p._id,
        createdAt: new Date(p.createdAt).toLocaleString(),
      }));

      Object.assign(mData, {
        ...data,
        totalItems,
        totalPages,
        data: [...transactionsRequest.data, ...formattedData],
      });
    } catch (e) {
      mData.data = [];
      showAlert({ title: "Atenção", description: e.message });
    } finally {
      mData.loading = false;
      await sleep(1000);
      setTransactionsRequest(mData);
    }
  }, [transactionsRequest.page]);

  const onTransactionsNextPage = ({ page }) => {
    if (transactionsRequest.loading) return;
    setTransactionsRequest((o) => ({
      ...o,
      page,
      loading: true,
      //limit: pageSize,
    }));
  };

  const getRewards = useCallback(async () => {
    const mData = { ...rewardsRequest };

    try {
      setRewardsRequest((t) => ({ ...t, loading: true }));
      //rewardsRequest.loading = true;
      //showLoading();
      const data = await axios.get(`/user-customer/availableRewards`);

      const formattedData = data.map(({ ...p }, index) => ({
        ...p,
        id: p._id,
        createdAt: new Date(p.createdAt).toLocaleString(),
        elegible: balances.balance >= p.requiredPoints,
        remaingPoints: p.requiredPoints - balances.balance,
        showRedeemInfo: () => {
          showAlert({
            title: "Resgate de prêmio",
            description: `Para resgatar o prêmio desejado, é necessário comparecer à loja portando seus documentos pessoais. Dirija-se ao atendimento no local para concluir a retirada de forma rápida e segura.`,
          });
        },
      }));

      Object.assign(mData, {
        ...data,

        data: [...rewardsRequest.data, ...formattedData],
      });
    } catch (e) {
      mData.data = [];
      showAlert({ title: "Atenção", description: e.message });
    } finally {
      mData.loading = false;
      await sleep(1000);
      setRewardsRequest(mData);
    }
  }, [balances]);

  useEffect(() => {
    async function fetchdata() {
      return getTransactions();
    }

    if (!transactionsRequest.page) return;

    fetchdata();
    return () => {};
  }, [getTransactions]);

  useEffect(() => {
    Promise.all([getBalances()]);
  }, []);

  return {
    loading,
    customer,
    balances,
    getBalances,
    updateCustomer,
    updateBalances: getBalances,
    transactionsRequest,
    onTransactionsNextPage,
    setLoading,
    getTransactions,
    getRewards,
    rewardsRequest,
  };
};

export const useCustomer = () => useContext(CustomerContext);
